'use strict'

import Utils from './../Utils.js'
import Dropcolor from './../Dropcolor.js'
import Color from './../models/Color.js'
import SliderUIController from '../controllers/SliderUIController.js'
import SwatchDataCache from '../ui/SwatchDataCache.js'
import { applySwatchColorToCurrentSlot, SWATCH_APPLY_MODE } from '../ui/SwatchApply.js'
import { renderCurrentSwatchSelector } from '../ui/visualizer-swatch-panel.js'
import { renderTextureSwatchSelector, updateTextureSwatchBackgroundColor } from '../ui/visualizer-texture-swatch-panel.js'
import { DOM } from './../ui/DOM.js'
import { Selector } from './../ui/Selectors.js'
import { TOOL_BUTTON_SELECTOR } from './../constants/ToolButtons.js'

/**
 * DropHandler
 *  - Coordinates dropcolor interactions (drop on canvas/slots, click selection).
 *  - Keeps sliders, canvas state, and swatch/texture UI in sync.
 *  - Exposes helper hooks for PaintingListener to delegate UI events.
 */
class DropHandler {
    /**
     * @param {Application} application - Visualizer Application instance.
     * @param {Document|HTMLElement} [root=document] - DOM root for DOM helpers.
     */
    constructor(application, root = document) {
        this.app = application
        this.root = root
    }

    /**
     * Drop on the canvas surface → set workingColorPos and delegate.
     * @param {{ draggable: JQuery<HTMLElement>[], helper: JQuery<HTMLElement>[] }} ui - jQuery UI drop payload.
     */
    handleCanvasDrop(ui) {
        const app = this.app

        const [x, y] = Utils.getImageViewDroppableCoordinates(app.canvasState, ui)
        const idx = x + y * app.canvasState.viewimage.width
        const numColor = app.canvasState._maskImage.data[idx]

        if (numColor >= 255) return

        const swatchId = ui.draggable[0].id
        const classList = [...ui.helper[0].classList]

        app.canvasState.colorSample.workingColorPos = numColor
        Dropcolor.replaceDropcolorWithDroppable(`dropcolor${numColor}`, swatchId)
        Dropcolor.selectSlot(numColor)

        void this.elementDropped(classList, swatchId, ui)
    }

    /**
     * Drop onto a slot (.dropcolor) → set workingColorPos and delegate.
     * @param {Event} event
     * @param {{ draggable: JQuery<HTMLElement>[], helper: JQuery<HTMLElement>[] }} ui - jQuery UI drop payload.
     * @param {string} droppableId - Slot element id.
     */
    handleSlotDrop(event, ui, droppableId) {
        const app = this.app

        const swatchId = ui.draggable[0].id
        const classList = [...ui.helper[0].classList]

        const slot = parseInt(droppableId.replace('dropcolor', ''), 10)
        app.canvasState.colorSample.workingColorPos = slot

        Dropcolor.replaceDropcolorWithDroppable(droppableId, swatchId)
        Dropcolor.selectSlot(slot)

        const magicBtn = DOM.get(Selector.btnMagic)
        if (!app.toolManager.activeTool && magicBtn && !magicBtn.disabled) {
            $(TOOL_BUTTON_SELECTOR.MAGIC).trigger('click')
        }

        void this.elementDropped(classList, swatchId, ui)
    }

    /**
     * Click on a slot → select and sync sliders or clear state.
     * @param {HTMLElement} el - Slot element.
     * @param {{ highlight?: boolean }} [options] - Whether to highlight the source swatch.
     */
    async handleSlotClick(el, { highlight = true } = {}) {
        const app = this.app
        app.canvasState.viewimage.loadData()

        const hasContent = typeof $(el).children('div').attr('class') !== 'undefined'
        app.canvasState.colorSample.workingColorPos = parseInt(el.id.replace('dropcolor', ''), 10)

        if (hasContent) {
            const color = app.canvasState.colorSample.getCurrentColor()
            SliderUIController.syncToColor(color)
            this._setPaintToolsEnabled(true)
            if (highlight) {
                await this._highlightSourceSwatch(color)
            }
            this._applyTextureSwatchBackground(color)

            document.dispatchEvent(
                new CustomEvent('viz:dropcolorSelected', {
                    detail: {
                        slot: app.canvasState.colorSample.workingColorPos,
                        colorType: color?.type,
                        color,
                    },
                }),
            )
        } else {
            SliderUIController.clear()
            this._setPaintToolsEnabled(false)
            document.dispatchEvent(
                new CustomEvent('viz:dropcolorEmptySelected', {
                    detail: {
                        slot: app.canvasState.colorSample.workingColorPos,
                    },
                }),
            )
        }

        Dropcolor.selectSlot(app.canvasState.colorSample?.workingColorPos ?? 0)
    }

    /**
     * Enable or disable paint tools that require an active color.
     * @param {boolean} enabled
     * @private
     */
    _setPaintToolsEnabled(enabled) {
        const toolSelectors = [
            Selector.btnMagic,
            Selector.btnBrush,
            Selector.btnDrawLine,
            Selector.btnDrawPolyPlus,
            Selector.btnDrawPolyMinus,
        ]

        toolSelectors.forEach((selector) => {
            const btn = DOM.get(selector)
            if (!btn) return
            btn.disabled = !enabled
            btn.classList.toggle('is-disabled', !enabled)
            btn.setAttribute('aria-disabled', enabled ? 'false' : 'true')

            if (!enabled) {
                btn.classList.remove('is-active', 'btn-activated')
            }
        })

        if (!enabled) {
            this.app?.toolManager?.setActiveTool?.(undefined)
        }
    }

    /**
     * Ensure the matching swatch/texture dataset is loaded, select the tile, and scroll to it.
     * @param {Color} color
     * @private
     */
    async _highlightSourceSwatch(color) {
        if (!color) return

        const isTexture = color.colorHasTextureOrTextureSwatch()
        const swatchId = color?.swatch?.id
        const textureSwatchId = color?.texture_swatch?.id
        const textureTileId = color?.texture?.id
        const swatchColorId = color?.swatch_color?.id

        // Load correct swatch dataset if different
        if (swatchId && swatchId !== this.app.swatchSelection.currentSwatchId) {
            await this._loadSwatchDataset(swatchId)
            const select = DOM.get(Selector.selectCc)
            if (select) select.value = swatchId
        }
        if (isTexture && textureSwatchId && textureSwatchId !== this.app.swatchSelection.currentTextureSwatchId) {
            await this._loadTextureSwatchDataset(textureSwatchId)
        }

        // Clear previous highlights
        document.querySelectorAll('.swatchDroppableSelected').forEach((node) => node.classList.remove('swatchDroppableSelected'))
        document.querySelectorAll('.textureSwatchSelected').forEach((node) => node.classList.remove('textureSwatchSelected'))

        // Highlight based on the Color type
        if (color.type === Color.TYPE.COLOR && swatchColorId) {
            this._highlightTileById(String(swatchColorId), false)
        } else if (color.type === Color.TYPE.TEXTURE && swatchColorId && textureTileId) {
            this._highlightTileById(String(swatchColorId), false)
        } else if (color.type === Color.TYPE.TEXTURE_SWATCH && swatchColorId && textureTileId) {
            this._highlightTileById(String(swatchColorId), false)
            this._highlightTileById(`texture-swatch${textureTileId}`, true)
        }
    }

    async _loadSwatchDataset(swatchId) {
        const app = this.app
        try {
            const dataCache = SwatchDataCache.getInstance()
            // Use cache (with in-flight de-dupe) before hitting the backend.
            const swatch = await dataCache.getSwatchOrFetch(swatchId)
            app.swatchSelection.currentSwatch = swatch
            app.swatchSelection.currentSwatchId = swatch.id

            if (swatch.type === 1) {
                DOM.hide(Selector.textureswatchselectors)
                DOM.hide(Selector.scrollerEffects)
            }

            renderCurrentSwatchSelector(app)

            document.dispatchEvent(
                new CustomEvent('viz:swatchDatasetLoaded', {
                    detail: { type: 'swatch', id: swatchId },
                }),
            )
        } catch (err) {
            console.warn('[DropHandler] Failed to load swatch', swatchId, err)
        }
    }

    async _loadTextureSwatchDataset(textureSwatchId) {
        const app = this.app
        try {
            const dataCache = SwatchDataCache.getInstance()
            app.swatchSelection.currentTextureSwatchId = textureSwatchId
            // Use cache (with in-flight de-dupe) before hitting the backend.
            app.swatchSelection.currentTextureSwatch = await dataCache.getTextureSwatchOrFetch(textureSwatchId)
            renderTextureSwatchSelector(app)
            this._applyTextureSwatchBackground(app.canvasState.colorSample.getCurrentColor())

            document.dispatchEvent(
                new CustomEvent('viz:swatchDatasetLoaded', {
                    detail: { type: 'textureSwatch', id: textureSwatchId },
                }),
            )
        } catch (err) {
            console.warn('[DropHandler] Failed to load texture swatch', textureSwatchId, err)
        }
    }

    /**
     * Highlight a specific tile by id and optionally mark it as a texture selection.
     * @param {string} id - DOM id of the swatch/texture tile.
     * @param {boolean} markAsTexture - Also add texture-specific selected class.
     * @private
     */
    _highlightTileById(id, markAsTexture = false) {
        const targetEl = document.getElementById(id)
        console.debug('[DropHandler] highlight target', { id, found: !!targetEl })
        if (!targetEl) return
        targetEl.classList.add('swatchDroppableSelected')
        if (markAsTexture) {
            targetEl.classList.add('textureSwatchSelected')
        }
        targetEl.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'center' })
    }

    /**
     * Update texture swatch tiles to reflect the active color (background tint).
     * @param {Color} color
     * @private
     */
    _applyTextureSwatchBackground(color) {
        const sw = color?.swatch_color
        if (!sw) return
        const { sr, sg, sb, id } = sw
        updateTextureSwatchBackgroundColor({
            r: sr,
            g: sg,
            b: sb,
            swatch_color_id: id,
        })
    }

    /**
     * Central drop handler for canvas/slot drops.
     * @param {string[]} classList - Classes from the dragged helper element.
     * @param {string} swatchcolorid - Dropped swatch/texture id.
     * @param {{ draggable: JQuery<HTMLElement>[], helper: JQuery<HTMLElement>[] }} ui - jQuery UI drop payload.
     */
    async elementDropped(classList, swatchcolorid, ui) {
        const app = this.app

        const dropType = swatchcolorid?.startsWith('texture-swatch')
            ? Color.TYPE.TEXTURE_SWATCH
            : classList.includes('texture')
              ? Color.TYPE.TEXTURE
              : Color.TYPE.COLOR

        const syncSlidersForCurrentDropcolor = () => {
            const pos = app.canvasState.colorSample.workingColorPos
            if (!pos) return
            const slotEl = document.getElementById(`dropcolor${pos}`)
            if (slotEl) {
                this.handleSlotClick(slotEl, { highlight: false })
            }
        }

        // Use SwatchApply to keep click/drag/drop in a single color-apply pipeline.
        if (dropType === Color.TYPE.TEXTURE_SWATCH) {
            const textureID = swatchcolorid.replace('texture-swatch', '')
            const colorID = Dropcolor.getColorID(swatchcolorid)
            const backgroundImageURL = Dropcolor.getBackgroundImageURL(ui)

            const color = await Utils.getSwatchColorFromSwatchWithID(app, colorID, app.canvasState.colorSample.workingColorPos)
            color.type = Color.TYPE.TEXTURE_SWATCH

            await applySwatchColorToCurrentSlot({
                app,
                mode: SWATCH_APPLY_MODE.DRAG_TEXTURE,
                color,
                backgroundImageURL,
                textureID,
            })

            Utils.setSlidersValues(app.canvasState.colorSample)
            syncSlidersForCurrentDropcolor()
            return
        }

        if (dropType === Color.TYPE.TEXTURE) {
            const backgroundImageURL = Dropcolor.getBackgroundImageURL(ui)

            const color = await Utils.getSwatchColorFromSwatchWithID(app, swatchcolorid, app.canvasState.colorSample.workingColorPos)
            color.type = Color.TYPE.TEXTURE

            await applySwatchColorToCurrentSlot({
                app,
                mode: SWATCH_APPLY_MODE.DRAG_TEXTURE,
                color,
                backgroundImageURL,
            })

            Utils.setSlidersValues(app.canvasState.colorSample)
            syncSlidersForCurrentDropcolor()
            return
        }

        const color = await Utils.getSwatchColorFromSwatchWithID(app, swatchcolorid, app.canvasState.colorSample.workingColorPos)
        await applySwatchColorToCurrentSlot({
            app,
            mode: SWATCH_APPLY_MODE.DRAG_COLOR,
            color,
        })

        syncSlidersForCurrentDropcolor()
    }
}

export default DropHandler
